import type * as models from 'pkg/api/models';
import type { Event } from 'pkg/api/models/event';
import { EventTypes, EventStatus } from 'pkg/api/models/event';
import type { EventUser } from 'pkg/api/models/event_user';
import { EventUserRole } from 'pkg/api/models/event_user';
import { getTimeZoneFromSessionStorage } from 'pkg/timezone';

export function supportsTrainingSessions(eventType: EventTypes): boolean {
	return eventType === EventTypes.Practice || eventType === EventTypes.Camp;
}

export function hasStarted(event: Event): boolean {
	const now = Date.now() / 1000;

	return now > event.startsAt;
}

export function hasEnded(event: Event): boolean {
	const now = Date.now() / 1000;

	return now > event.endsAt;
}

export function rsvpHasPassed(event: Event): boolean {
	if (!event.rsvpBefore) {
		return false;
	}

	const now = Date.now() / 1000;

	return now > event.rsvpBefore;
}

export function inviteBeforeHasPassed(event: Event): boolean {
	if (!event.inviteBefore) {
		return false;
	}

	const now = Date.now() / 1000;

	return now > event.inviteBefore;
}

export function isCanceled(event: Event): boolean {
	return event.status === EventStatus.Canceled;
}

export function hasEventSeries(event: Event) {
	return !!event.eventSeriesId;
}

export function isFromRegistration(event: Event) {
	return !!event.fromRegistration;
}

export function isCapacityKnown(event: Event): boolean {
	return (
		event.capacity !== undefined &&
		event.capacity !== null &&
		event.attendanceStats !== undefined
	);
}

export function isFull(event: Event): boolean {
	// Prefer the leak-safe flag when the payload carries it (e.g. event cards).
	if (event.capacityReached !== undefined) {
		return event.capacityReached;
	}

	if (!isCapacityKnown(event)) {
		return false;
	}

	return (
		(event.attendanceStats?.acceptedParticipantCount ?? 0) >=
		(event.capacity ?? 0)
	);
}

export function capacityBlocksAccept(
	event: Event,
	eventUser: EventUser
): boolean {
	return eventUser.role === EventUserRole.Participant && isFull(event);
}

export function timezoneIsDifferent(event: Event) {
	if (!event.timezone) {
		return false;
	}

	return getTimeZoneFromSessionStorage()?.name !== event.timezone;
}

export function canUpdateAveragePhysicalStrain(event: Event): boolean {
	if (event.activities.length === 0) {
		return true;
	}

	const canUpdate = event.activities
		.map(
			(activity: models.userActivity.UserActivity) => activity.physicalStrain
		)
		.every((strain: number) => strain === event.physicalStrainDefault);

	return canUpdate;
}
